import { Capacitor } from '@capacitor/core'
import { isOrientationVisible, hasOrientationModal } from '../global/memberOrientation'

const priorities = { flash: 0, critical: 10, 'mail-bounce': 15, push: 20, setup: 30, tour: 40 }

// All notices share this slot and its modal/flash priority rules.
export function selectNotice(candidates, modalPending, native) {
  if (modalPending) return null
  return candidates.filter(candidate => candidate.type in priorities && (!candidate.native_only || native))
    .sort((first, second) => priorities[first.type] - priorities[second.type] || (first.key < second.key ? -1 : first.key > second.key ? 1 : 0))[0] || null
}

export default function infoBanner({ available = false, style = 'success', message = null, modalPending = false, tourId = 'member-orientation', bounce = null } = {}) {
  let root, wireId, controller, observer, frame, refreshPromise, acknowledgement
  let disposed = false
  let actionBusy = false
  let serverNeedsReady = modalPending
  let refreshPending = false

  const wire = () => wireId && window.Livewire?.find(wireId)
  const nextFrame = () => new Promise(resolve => window.requestAnimationFrame(resolve))
  const request = async name => {
    const removeInterceptor = wire()?.$interceptAction(name, ({ onError }) => {
      onError(({ preventDefault }) => preventDefault())
    })
    try { return await wire()?.[name]() } finally { removeInterceptor?.() }
  }

  return {
    available,
    style,
    message,
    bounce,
    modalPending,
    blocked: true,
    tourReady: false,
    offered: false,
    used: false,
    reference: null,
    pendingNotices: [],

    get selected() {
      const candidates = [...this.pendingNotices]
      if (this.message) candidates.push({ key: 'flash', type: 'flash' })
      if (this.bounce) candidates.push({ key: 'mail-bounce', type: 'mail-bounce' })
      if (this.tourReady && !this.used) candidates.push({ key: tourId, type: 'tour' })
      return selectNotice(candidates, this.modalPending || this.blocked, Capacitor.isNativePlatform())
    },

    init() {
      root = this.$root
      wireId = root.closest('[wire\\:id]')?.getAttribute('wire:id')
      controller = new AbortController()
      const options = { signal: controller.signal }
      serverNeedsReady ||= !!document.querySelector('[data-layout-modal-pending]')
      document.addEventListener('banner-message', event => {
        this.message = event.detail?.message
        this.style = event.detail?.style || 'success'
        if (this.offered) this.used = true
        this.schedule()
      }, options)
      const modalPending = () => {
        this.modalPending = true
        if (this.offered) this.used = true
        this.schedule()
      }
      window.addEventListener('openModal', modalPending, options)
      window.addEventListener('help-drawer-show', modalPending, options)
      window.addEventListener('member-orientation-state', event => {
        if (event.detail?.active) this.used = true
        this.schedule()
      }, options)
      // No producer is installed in this slice. A native-only reservation cannot
      // occupy the desktop slot; future producers must render in this same region.
      window.addEventListener('info-banner-pending', event => {
        const candidate = event.detail
        if (!candidate?.key || !['critical', 'push', 'setup'].includes(candidate.type)) return
        this.pendingNotices = this.pendingNotices.filter(notice => notice.key !== candidate.key)
        if (candidate.pending) this.pendingNotices.push(candidate)
        if (this.offered && this.pendingNotices.some(notice => !notice.native_only || Capacitor.isNativePlatform())) this.used = true
        this.schedule()
      }, options)
      document.addEventListener('scroll', () => this.schedule(), { ...options, capture: true })
      document.addEventListener('visibilitychange', () => this.schedule(), options)
      window.addEventListener('resize', () => this.schedule(), options)
      window.addEventListener('pageshow', event => {
        if (event.persisted && this.offered) this.used = true
      }, options)
      document.addEventListener('livewire:navigating', () => this.destroy(), options)
      observer = new MutationObserver(() => this.schedule())
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['hidden', 'inert', 'aria-hidden', 'style', 'class', 'data-overlay-open', 'data-layout-modal-pending']
      })
      this.schedule()
    },

    schedule() {
      if (disposed || frame) return
      frame = window.requestAnimationFrame(() => { frame = null; this.refresh() })
    },

    refresh() {
      if (refreshPromise) {
        refreshPending = true
        return refreshPromise
      }
      refreshPromise = this.update().catch(() => {
        this.tourReady = false
        this.available = false
      }).finally(() => {
        refreshPromise = null
        if (refreshPending) {
          refreshPending = false
          this.schedule()
        }
      })
      return refreshPromise
    },

    async update() {
      if (disposed) return
      const modal = hasOrientationModal() || [...document.querySelectorAll('[data-test="help-drawer"], [data-test="help-drawer.backdrop"]')].some(isOrientationVisible)
      if (modal) {
        if (this.offered) this.used = true
        this.modalPending = false
        document.querySelectorAll('[data-layout-modal-pending]').forEach(element => element.removeAttribute('data-layout-modal-pending'))
      }
      this.blocked = modal || !!document.querySelector('[data-layout-modal-pending]') ||
        document.visibilityState !== 'visible' || !!root.closest('[inert], [aria-hidden="true"]')
      if (this.blocked || this.modalPending || this.used || !this.available) return
      if (serverNeedsReady) {
        await request('layoutReady')
        serverNeedsReady = false
      }
      if (this.message || this.bounce || this.pendingNotices.some(notice => !notice.native_only || Capacitor.isNativePlatform())) return
      if (!this.reference) this.reference = await request('prepareTourOffer')
      if (!this.reference) {
        this.available = false
        return
      }
      if (disposed || this.message || this.used) return
      this.tourReady = true
      await this.$nextTick(() => {})
      await nextFrame()
      await this.confirmVisible()
    },

    visibleTour() {
      const element = root.querySelector('[data-test="tour-offer"]')
      if (disposed || this.selected?.type !== 'tour' || !isOrientationVisible(element)) return false
      if (hasOrientationModal() || document.visibilityState !== 'visible') return false
      const rect = element.getBoundingClientRect()
      if (rect.top < 0 || rect.left < 0 || rect.bottom > innerHeight || rect.right > innerWidth) return false
      const header = document.querySelector('header')
      if (header && getComputedStyle(header).position === 'fixed' && rect.top + 1 < header.getBoundingClientRect().bottom) return false
      return [...element.querySelectorAll('button')].every(button => {
        if (button.disabled || !isOrientationVisible(button)) return false
        const bounds = button.getBoundingClientRect()
        const hit = document.elementFromPoint((bounds.left + bounds.right) / 2, (bounds.top + bounds.bottom) / 2)
        return hit === button || button.contains(hit)
      })
    },

    trustedClick(event, action) {
      const button = root.querySelector(`[data-test="tour-offer.${action}"]`)
      return !disposed && this.selected?.type === 'tour' && button && !button.disabled &&
        event?.isTrusted === true && event.currentTarget === button
    },

    async confirmVisible(clicked = false) {
      if (this.offered) return true
      if (acknowledgement) return acknowledgement
      if (!clicked && !this.visibleTour()) return false
      const removeInterceptor = wire()?.$interceptAction('confirmTourOffered', ({ onError }) => {
        onError(({ preventDefault }) => preventDefault())
      })
      acknowledgement = Promise.resolve().then(() => wire()?.confirmTourOffered(this.reference)).then(saved => {
        this.offered = saved === true
        if (!this.offered) {
          this.tourReady = false
          this.available = false
        }
        if (this.offered && (this.message || this.modalPending || this.blocked || this.pendingNotices.some(notice => !notice.native_only || Capacitor.isNativePlatform()))) this.used = true
        return this.offered
      }).catch(() => {
        this.tourReady = false
        this.available = false
        return false
      }).finally(() => {
        removeInterceptor?.()
        acknowledgement = null
      })
      return acknowledgement
    },

    async startTour(event) {
      const clicked = this.trustedClick(event, 'start')
      if (actionBusy || this.used || (!clicked && !this.visibleTour())) return
      actionBusy = true
      try {
        if (await this.confirmVisible(clicked)) {
          this.used = true
          window.dispatchEvent(new CustomEvent('guided-tour-start-requested', { detail: { id: tourId } }))
        }
      } finally { actionBusy = false }
    },

    async dismissTour(event) {
      const clicked = this.trustedClick(event, 'dismiss')
      if (actionBusy || this.used || (!clicked && !this.visibleTour())) return
      actionBusy = true
      const removeInterceptor = wire()?.$interceptAction('dismissTourOffer', ({ onError }) => {
        onError(({ preventDefault }) => preventDefault())
      })
      try {
        if (await this.confirmVisible(clicked) && await wire()?.dismissTourOffer(this.reference)) this.used = true
      } catch {
        this.used = true
      } finally {
        removeInterceptor?.()
        actionBusy = false
      }
    },

    async dismissFlash() {
      this.message = null
      if (this.available) {
        const removeInterceptor = wire()?.$interceptAction('closeFlash', ({ onError }) => {
          onError(({ preventDefault }) => preventDefault())
        })
        try { await wire()?.closeFlash() } catch {
          this.available = false
          this.tourReady = false
        } finally { removeInterceptor?.() }
      }
      this.schedule()
    },

    async dismissMailBounce() {
      if (actionBusy || !this.bounce) return
      actionBusy = true
      try {
        if (await request('dismissMailBounce') === true) this.bounce = null
      } catch {
        // Keep the notice visible when its dismissal could not be saved.
      } finally {
        actionBusy = false
        this.schedule()
      }
    },

    destroy() {
      disposed = true
      controller?.abort()
      observer?.disconnect()
      if (frame) window.cancelAnimationFrame(frame)
      this.tourReady = false
    }
  }
}
